import React, { useState, useEffect, createContext, useContext } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './mobile-tokens.css';
import { flushQueue } from '../../services/defectQueue';
import { flushWoQueue } from '../../services/woQueue';
import { flushInspectionQueue } from '../../services/inspectionQueue';
import MobileLogin from './MobileLogin';
import MobilePinEntry from './MobilePinEntry';
import MobileDashboard from './MobileDashboard';
import MobileWorkOrders from './MobileWorkOrders';
import MobileWorkOrderDetail from './MobileWorkOrderDetail';
import MobileInspections from './MobileInspections';
import MobileInspectionFlow from './MobileInspectionFlow';
import MobileDefects from './MobileDefects';
import MobileDefectDetail from './MobileDefectDetail';
import MobileReportDefect from './MobileReportDefect';
import MobileScanner from './MobileScanner';
import MobileProfile from './MobileProfile';
import MobileAssetDetail from './MobileAssetDetail';
import MobileServicesDue from './MobileServicesDue';

// Mobile Auth Context
const MobileAuthContext = createContext(null);

export const useMobileAuth = () => {
  const context = useContext(MobileAuthContext);
  if (!context) {
    throw new Error('useMobileAuth must be used within MobileAuthProvider');
  }
  return context;
};

// Mobile Auth Provider
const MobileAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  // Flush any offline-queued records when connectivity returns (and once on load).
  useEffect(() => {
    const flush = () => {
      const token = localStorage.getItem('mobile_token');
      flushQueue(token);
      flushWoQueue(token);
      flushInspectionQueue(token);
    };
    flush();
    window.addEventListener('online', flush);
    return () => window.removeEventListener('online', flush);
  }, []);

  const checkAuth = () => {
    let token = localStorage.getItem('mobile_token');
    let userData = localStorage.getItem('mobile_user');
    const pinEnabled = localStorage.getItem('mobile_pin_enabled') === 'true';
    const pinVerified = sessionStorage.getItem('mobile_pin_verified') === 'true';

    // Bootstrap from desktop auth if a mechanic was just bounced over
    // (ProtectedRoute redirects mechanics to /mobile post-login). Avoids
    // making them log in twice for what is effectively one session.
    if (!token || !userData) {
      const desktopToken = localStorage.getItem('equipr_token');
      const desktopUserStr = localStorage.getItem('equipr_user');
      if (desktopToken && desktopUserStr) {
        try {
          const desktopUser = JSON.parse(desktopUserStr);
          if (desktopUser?.role === 'mechanic') {
            localStorage.setItem('mobile_token', desktopToken);
            localStorage.setItem('mobile_user', JSON.stringify(desktopUser));
            sessionStorage.setItem('mobile_pin_verified', 'true');
            token = desktopToken;
            userData = JSON.stringify(desktopUser);
          }
        } catch (e) {
          /* fall through to login */
        }
      }
    }

    if (token && userData) {
      const parsedUser = JSON.parse(userData);
      // Check if token is expired
      if (parsedUser.expires_at && new Date(parsedUser.expires_at) < new Date()) {
        logout();
        return;
      }
      
      // If PIN is enabled but not verified this session, require PIN
      if (pinEnabled && !pinVerified) {
        setUser({ ...parsedUser, needsPinVerification: true });
      } else {
        setUser(parsedUser);
      }
    }
    setLoading(false);
  };

  const login = (userData, token) => {
    localStorage.setItem('mobile_token', token);
    localStorage.setItem('mobile_user', JSON.stringify(userData));
    sessionStorage.setItem('mobile_pin_verified', 'true');
    setUser(userData);
  };

  const setupPin = (pin) => {
    localStorage.setItem('mobile_pin_enabled', 'true');
    // PIN is stored hashed on server, we just mark it as enabled locally
  };

  const verifyPin = () => {
    sessionStorage.setItem('mobile_pin_verified', 'true');
    setUser(prev => ({ ...prev, needsPinVerification: false }));
  };

  const logout = () => {
    localStorage.removeItem('mobile_token');
    localStorage.removeItem('mobile_user');
    localStorage.removeItem('mobile_pin_enabled');
    sessionStorage.removeItem('mobile_pin_verified');
    setUser(null);
    navigate('/mobile/login');
  };

  const getToken = () => localStorage.getItem('mobile_token');

  return (
    <MobileAuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout, 
      setupPin, 
      verifyPin,
      getToken,
      isAuthenticated: !!user && !user.needsPinVerification
    }}>
      {children}
    </MobileAuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useMobileAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--surface-page)] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[var(--volt-edge)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/mobile/login" state={{ from: location }} replace />;
  }

  if (user.needsPinVerification) {
    return <Navigate to="/mobile/pin" state={{ from: location }} replace />;
  }

  return children;
};

// Main Mobile App Component
const MobileApp = () => {
  return (
    <div className="mobile-app mobile-root bg-[var(--surface-page)] min-h-screen">
      <MobileAuthProvider>
        <Routes>
          <Route path="login" element={<MobileLogin />} />
          <Route path="pin" element={<MobilePinEntry />} />
          
          {/* Protected Routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <MobileDashboard />
            </ProtectedRoute>
          } />
          <Route path="work-orders" element={
            <ProtectedRoute>
              <MobileWorkOrders />
            </ProtectedRoute>
          } />
          <Route path="work-orders/:id" element={
            <ProtectedRoute>
              <MobileWorkOrderDetail />
            </ProtectedRoute>
          } />
          <Route path="inspections" element={
            <ProtectedRoute>
              <MobileInspections />
            </ProtectedRoute>
          } />
          <Route path="inspections/new/:assetId/:templateId" element={
            <ProtectedRoute>
              <MobileInspectionFlow />
            </ProtectedRoute>
          } />
          <Route path="defects" element={
            <ProtectedRoute>
              <MobileDefects />
            </ProtectedRoute>
          } />
          <Route path="defects/new" element={
            <ProtectedRoute>
              <MobileReportDefect />
            </ProtectedRoute>
          } />
          <Route path="defects/:id" element={
            <ProtectedRoute>
              <MobileDefectDetail />
            </ProtectedRoute>
          } />
          <Route path="scan" element={
            <ProtectedRoute>
              <MobileScanner />
            </ProtectedRoute>
          } />
          <Route path="assets/:id" element={
            <ProtectedRoute>
              <MobileAssetDetail />
            </ProtectedRoute>
          } />
          <Route path="services" element={
            <ProtectedRoute>
              <MobileServicesDue />
            </ProtectedRoute>
          } />
          <Route path="profile" element={
            <ProtectedRoute>
              <MobileProfile />
            </ProtectedRoute>
          } />
          
          {/* Default redirect */}
          <Route path="*" element={<Navigate to="/mobile" replace />} />
        </Routes>
      </MobileAuthProvider>
    </div>
  );
};

export default MobileApp;
